import { type Modification, type ModificationsMap } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Adds a modification to the list of active modifications.
 *
 * If the modification for the provided ingredientId already exists in the list,
 * it updates its quantity. Otherwise, it adds the modification.
 */
export function addModificationToActiveModificationsList(
  ingredientId: string,
  activeModifications: Modification[],
  allModificationsMap: ModificationsMap,
) {
  const modification = allModificationsMap.get(ingredientId);

  if (!modification) {
    return activeModifications;
  }

  const checkIfHasModification = checkIfMatchesIngredient(ingredientId);
  const updateModificationQuantity = updateModificationQuantityFactory(ingredientId); // prettier-ignore

  const hasModification = activeModifications.some(checkIfHasModification);

  if (!hasModification) {
    return [...activeModifications, modification];
  }

  return activeModifications.map(updateModificationQuantity);
}

// ─────────────────────────────────────────────────────────────────────────────

function checkIfMatchesIngredient(ingredientId: string) {
  return function (modification: Modification) {
    return modification.ingredient?.id === ingredientId;
  };
}

function updateModificationQuantityFactory(ingredientId: string) {
  return function (modification: Modification) {
    const isTargetModification = modification.ingredient?.id === ingredientId;

    return isTargetModification ? withIncreasedQuantity(modification) : modification;
  };
}

function withIncreasedQuantity(modification: Modification): Modification {
  return { ...modification, quantity: modification.quantity + 1 };
}
